import React, { FC } from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  ContractDataDocument,
  TaskDetailAffecteeEmployeeFragment,
  TaskDetailAffecteeReferenceContractFragment,
  TaskDetailContractContextlessPoliciesFragment,
  TaskDetailCustomFieldsContextlessPoliciesFragment,
  TaskDetailJobCatalogContextlessPoliciesFragment,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useQuery } from 'modules/core/hooks/useQuery'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { useFixFlowProvider } from 'modules/contracts/components/FixFlow/FixFlowProvider'
import ContractDates from 'modules/contracts/components/FlowPage/Steps/Dates/ContractDates'
import CustomFields from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras/CustomFields'
import RoleLevel from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/RoleLevel'
import Salary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/Salary'
import { WorkHours } from 'modules/contracts/components/FlowPage/Steps/WorkHours'
import { DataCollectionCategories } from 'modules/processes/domain'

interface Props {
  legalEntity: LegalEntity | null
  category: DataCollectionCategories
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  legalEntityCountry: string
  referenceContract?: TaskDetailAffecteeReferenceContractFragment | null
  jobCatalogContextlessPolicies: TaskDetailJobCatalogContextlessPoliciesFragment | undefined
  affecteeEmployee: TaskDetailAffecteeEmployeeFragment | null | undefined
  hasPayrollBundle: boolean
  contractContextlessPolicies: TaskDetailContractContextlessPoliciesFragment | undefined
  customFieldsContextlessPolicies: TaskDetailCustomFieldsContextlessPoliciesFragment | undefined
}

const Loading = () => {
  return (
    <Box width="full" height="full" justifyContent="center" alignItems="center">
      <Spinner />
    </Box>
  )
}

const ContractData: FC<Props> = ({
  legalEntity,
  category,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  legalEntityCountry,
  referenceContract,
  jobCatalogContextlessPolicies,
  affecteeEmployee,
  hasPayrollBundle,
  contractContextlessPolicies,
  customFieldsContextlessPolicies,
}) => {
  const { canEdit, loading, form } = useFixFlowProvider()
  const { data } = useQuery(ContractDataDocument, {
    variables: { affecteesId: affecteeEmployee?.id ?? 0 },
    skip: !affecteeEmployee?.id,
  })
  const contractVersions = data?.contracts.contractVersionsConnection.nodes ?? []

  if (loading) return <Loading />
  if (!Object.keys(form.data() || {}).length) return <Loading /> // when contract is still loading, the form is empty
  if (!canEdit) return <NotFound />

  switch (category) {
    case DataCollectionCategories.start_date:
      return (
        <ContractDates
          form={form}
          contractVersionId={referenceContract?.id?.toString()}
          contractVersions={contractVersions}
        />
      )
    case DataCollectionCategories.role:
      return (
        <RoleLevel
          withFormWrapper={false}
          withSeparator={false}
          form={form}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
          canSeeJobTitle={
            referenceContract?._policies?.seeJobTitle.can ??
            contractContextlessPolicies?.contractSeeJobTitle.can ??
            false
          }
          employee={affecteeEmployee}
          contractVersion={referenceContract}
        />
      )
    case DataCollectionCategories.salary:
      return (
        <Salary
          withFormWrapper={false}
          form={form}
          employee={affecteeEmployee}
          canEditPayrollPolicy={false}
          canSeeCompensations={
            referenceContract?._policies?.seeAdditionalCompensation.can ??
            contractContextlessPolicies?.contractSeeAdditionalCompensation.can ??
            false
          }
          canSeeGrossSalary={
            referenceContract?._policies?.seeGrossSalary.can ??
            contractContextlessPolicies?.contractSeeGrossSalary.can ??
            false
          }
          canSeePayrollPolicy={
            referenceContract?._policies?.seePayrollPolicy.can ??
            contractContextlessPolicies?.contractSeePayroll.can ??
            false
          }
          hasPayrollBundle={hasPayrollBundle}
          legalEntityId={legalEntity?.id}
          legalEntityCountry={legalEntityCountry}
          contractVersion={referenceContract}
        />
      ) // policy edition can not be done in task detail page
    case DataCollectionCategories.contract_hours:
      return (
        legalEntity && (
          <WorkHours
            withFormWrapper={false}
            form={form}
            legalEntityCountry={legalEntityCountry}
            canSeeContractType={
              referenceContract?._policies?.seeContractType.can ??
              contractContextlessPolicies?.contractSeeContractType.can ??
              false
            }
            canSeeWorkingHours={
              referenceContract?._policies?.seeWorkingHours.can ??
              contractContextlessPolicies?.contractSeeWorkingHours.can ??
              false
            }
            contractVersion={referenceContract}
            hasContractVersions={contractVersions.length > 1}
            hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
            hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          />
        )
      )
    case DataCollectionCategories.contract_custom_fields:
      return (
        <CustomFields
          withFormWrapper={false}
          form={form}
          customFieldsContextlessPolicies={customFieldsContextlessPolicies}
          legalEntityId={legalEntity?.id}
        />
      )
    default:
      return null
  }
}

export default ContractData
